import React, { useState, useEffect } from "react";
import {
  X,
  CreditCard,
  ShieldCheck,
  ChevronRight,
  ArrowLeft,
  Lock,
  Plane,
  Wallet,
  Zap,
  Info,
  User,
  MapPin,
  Building,
  Navigation
} from "lucide-react";
import NTPLogo from "ntp-logo-react";
import { Button } from "@/components/ui/button";
import { Country, State } from "country-state-city";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
  onSuccess: () => void;
  itemsToPay: CartItemDto[];
  processCardPayment: (items: CartItemDto[], details: CardDetailsDto, billingDetails: BillingAddressDto) => Promise<any>;
}

const PaymentModal = ({ isOpen, onClose, currency, onSuccess, itemsToPay, processCardPayment }: Props) => {
  // Step 0: Billing Info, Step 1: Payment Method, Step 2: Card Details
  const [step, setStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Billing Address State
  const [billingData, setBillingData] = useState<BillingAddressDto>({
    country: "RO", // Defaulting to Romania for easier local UX
    county: "",
    city: "",
    streetName: "",
    number: "",
    postalZone: ""
  });

  const [cardData, setCardData] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: ""
  });

  // Country/State lists
  const countriesList = Country.getAllCountries();
  const statesList = State.getStatesOfCountry(billingData.country);

  // Reset local state if country changes
  useEffect(() => {
    setBillingData(prev => ({ ...prev, county: "", city: "" }));
  }, [billingData.country]);

  if (!isOpen) return null;

  const handleBillingChange = (field: keyof BillingAddressDto, value: string) => {
    setBillingData(prev => ({ ...prev, [field]: value }));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardData({ ...cardData, name: e.target.value.toUpperCase() });
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    value = value.substring(0, 16);
    const formatted = value.match(/.{1,4}/g)?.join(" ") || "";
    setCardData({ ...cardData, number: formatted });
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 4) value = value.substring(0, 4);

    let formatted = value;
    if (value.length > 2) {
      formatted = value.substring(0, 2) + "/" + value.substring(2);
    }
    setCardData({ ...cardData, expiry: formatted });
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    value = value.substring(0, 4);
    setCardData({ ...cardData, cvv: value });
  };

  const isBillingFormValid = () => {
    const { country, county, city, streetName, number } = billingData;
    return country && county && city && streetName && number;
  };

  const totalAmount = itemsToPay.reduce((acc, item) => acc + (item.grossValue || 0), 0);

  const handleFinalize = async () => {
    if (paymentMethod !== 'card') {
      console.info("Metoda selectată nu este implementată încă.");
      return;
    }

    setIsProcessing(true);
    try {
      const sanitizedItems = itemsToPay.map(({ mediaFiles, ...rest }) => rest);

      const cardDetails: CardDetailsDto = {
        cardNumber: cardData.number.replace(/\s/g, ""),
        ownerName: cardData.name,
        cvv: cardData.cvv,
        expirationDate: cardData.expiry
      };

      // Map dynamic codes back to explicit display names before sending payload
      const selectedCountryName = Country.getCountryByCode(billingData.country)?.name || billingData.country;
      const selectedStateName = State.getStateByCodeAndCountry(billingData.county, billingData.country)?.name || billingData.county;

      const finalizedBilling: BillingAddressDto = {
        ...billingData,
        country: selectedCountryName,
        county: selectedStateName
      };

      const result = await processCardPayment(sanitizedItems, cardDetails, finalizedBilling);

      if (result) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Payment failed:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const inputClasses = "w-full bg-slate-950/40 border border-slate-800 rounded-lg px-4 py-3 text-sm focus:border-blue-500/50 focus:outline-none transition-all font-mono placeholder:text-slate-700 placeholder:font-mono text-white appearance-none";
  const labelClasses = "text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-300 font-sans">

      {/* Modal Container */}
      <div className="relative w-[420px] h-[700px] bg-[#0F172A]/80 backdrop-blur-3xl border-2 border-slate-800 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col text-white">

        {/* Background Decorative Element */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-600/5 blur-3xl pointer-events-none rounded-full" />

        {/* Header */}
        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-950/20 relative">
          <div className="flex items-center gap-3">
            <div className="relative p-2.5 bg-blue-600/10 rounded-xl border border-blue-500/20">
              <Zap size={18} className="text-blue-400 animate-pulse" />
              <div className="absolute inset-0 bg-blue-500/20 blur-xl opacity-50 rounded-full" />
            </div>
            <div>
              <h3 className="text-lg font-black tracking-tighter uppercase italic">Finalizare Tranzacție</h3>
              <p className="font-mono text-[9px] text-blue-500 tracking-widest -mt-1">HangarX</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2.5 hover:bg-slate-800 rounded-full transition-colors text-slate-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-8 overflow-y-auto no-scrollbar relative">

          {/* STEP 0: Billing Info Form (Compact layout to eliminate vertical scrolling) */}
          {step === 0 && (
            <div className="animate-in slide-in-from-right-4 duration-300 h-full flex flex-col justify-between">
              <div>
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] mb-5 flex items-center gap-2">
                  <Info size={12} /> Pasul 00 // Date de Facturare
                </h4>

                <div className="space-y-3.5">
                  {/* Grid 1: Country and State */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className={labelClasses}>Țară</label>
                      <div className="relative">
                        <select
                          value={billingData.country}
                          onChange={(e) => handleBillingChange("country", e.target.value)}
                          className={inputClasses}
                        >
                          {countriesList.map((c) => (
                            <option key={c.isoCode} value={c.isoCode} className="bg-slate-900 text-white">
                              {c.name}
                            </option>
                          ))}
                        </select>
                        <MapPin size={14} className="absolute right-4 top-3.5 text-slate-600 pointer-events-none" />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className={labelClasses}>
                        {billingData.country === "RO" ? "Județ" : "Regiune"}
                      </label>
                      <div className="relative">
                        <select
                          value={billingData.county}
                          onChange={(e) => handleBillingChange("county", e.target.value)}
                          className={inputClasses}
                        >
                          <option value="" className="bg-slate-900 text-slate-500">Selectați</option>
                          {statesList.map((s) => (
                            <option key={s.isoCode} value={s.isoCode} className="bg-slate-900 text-white">
                              {s.name}
                            </option>
                          ))}
                        </select>
                        <Navigation size={14} className="absolute right-4 top-3.5 text-slate-600 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  {/* Grid 2: City and Postal Code */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2 space-y-1">
                      <label className={labelClasses}>
                        {billingData.county === "B" ? "Oraș / Sector" : "Oraș"}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={billingData.county === "B" ? "București, Sector 1" : "Ex: Cluj-Napoca"}
                          value={billingData.city}
                          onChange={(e) => handleBillingChange("city", e.target.value)}
                          className={inputClasses}
                        />
                        <Building size={14} className="absolute right-4 top-3.5 text-slate-600" />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className={labelClasses}>Cod Poștal</label>
                      <input
                        type="text"
                        placeholder="011855"
                        value={billingData.postalZone}
                        onChange={(e) => handleBillingChange("postalZone", e.target.value)} // changed from postalCode
                        className={inputClasses}
                      />
                    </div>
                  </div>

                  {/* Grid 3: Street & Building Number */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-3 space-y-1">
                      <label className={labelClasses}>Strada//Bloc//Scara</label>
                      <input
                        type="text"
                        placeholder="Ex: Bd. Aviatorilor, bl. 12, sc. 2"
                        value={billingData.streetName}
                        onChange={(e) => handleBillingChange("streetName", e.target.value)}
                        className={inputClasses}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className={labelClasses}>Număr</label>
                      <input
                        type="text"
                        placeholder="10"
                        value={billingData.number}
                        onChange={(e) => handleBillingChange("number", e.target.value)}
                        className={inputClasses}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <Button
                  disabled={!isBillingFormValid()}
                  onClick={() => setStep(1)}
                  className={`w-full py-5 rounded-xl font-black uppercase tracking-[0.2em] text-xs h-auto flex items-center justify-center gap-2 transition-all skew-x-[-10deg] ${
                    isBillingFormValid()
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-xl shadow-blue-900/20'
                    : 'bg-slate-800 text-slate-600 cursor-not-allowed'
                  }`}
                >
                  <span className="skew-x-[10deg] flex items-center gap-2">
                    Continuă spre Plată <ChevronRight size={16} />
                  </span>
                </Button>
              </div>
            </div>
          )}

          {/* STEP 1: Payment Method Selection */}
          {step === 1 && (
            <div className="animate-in slide-in-from-right-4 duration-300 h-full flex flex-col">
              <button onClick={() => setStep(0)} className="flex items-center gap-2 text-slate-500 hover:text-blue-400 text-[10px] font-black uppercase tracking-widest mb-6 transition-colors">
                <ArrowLeft size={14} /> Înapoi la adresă
              </button>

              <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
                <Info size={12} /> Pasul 01 // Metodă de Plată
              </h4>

              <div className="space-y-4 flex-1">
                {[
                  { id: 'card', label: 'Credit/Debit', icon: CreditCard, desc: 'Visa, Mastercard' },
                  { id: 'wallet', label: 'Digital Wallet', icon: Wallet, desc: 'Apple Pay, Google Pay' }
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setPaymentMethod(m.id)}
                    className={`w-full p-5 rounded-2xl border transition-all flex items-center justify-between group ${
                      paymentMethod === m.id
                      ? 'bg-blue-600/10 border-blue-500 shadow-[0_0_30px_rgba(59,130,246,0.1)]'
                      : 'bg-slate-950/40 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-5 text-left">
                      <div className={`p-3 rounded-xl border transition-colors ${paymentMethod === m.id ? 'bg-blue-600 text-white border-blue-500' : 'bg-slate-900 text-slate-400 border-slate-700'}`}>
                        <m.icon size={22} />
                      </div>
                      <div>
                        <p className={`text-sm font-bold uppercase tracking-wide ${paymentMethod === m.id ? 'text-white' : 'text-slate-200'}`}>{m.label}</p>
                        <p className="text-[10px] text-slate-500 font-mono tracking-tight mt-0.5">{m.desc}</p>
                      </div>
                    </div>
                    <ChevronRight size={18} className={`transition-transform ${paymentMethod === m.id ? 'translate-x-1 text-blue-400' : 'text-slate-700'}`} />
                  </button>
                ))}
              </div>

              <div className="pt-6 mt-auto">
                <Button
                  disabled={!paymentMethod}
                  onClick={() => setStep(2)}
                  className={`w-full py-5 rounded-xl font-black uppercase tracking-[0.2em] text-xs h-auto flex items-center justify-center gap-2 transition-all skew-x-[-10deg] ${
                    paymentMethod
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-xl shadow-blue-900/20'
                    : 'bg-slate-800 text-slate-600 cursor-not-allowed'
                  }`}
                >
                  <span className="skew-x-[10deg] flex items-center gap-2">
                    Introdu Datele Cardului <ChevronRight size={16} />
                  </span>
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2: Card Details Input */}
          {step === 2 && (
            <div className="animate-in slide-in-from-right-4 duration-300 h-full flex flex-col">
              <button onClick={() => setStep(1)} className="flex items-center gap-2 text-slate-500 hover:text-blue-400 text-[10px] font-black uppercase tracking-widest mb-6 transition-colors">
                <ArrowLeft size={14} /> Înapoi
              </button>

              <div className="space-y-4 flex-1">
                <div className="space-y-1.5">
                  <label className={labelClasses}>Nume Complet</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="HENRI COANDĂ"
                      value={cardData.name}
                      onChange={handleNameChange}
                      className={inputClasses}
                    />
                    <User size={16} className="absolute right-4 top-3.5 text-slate-600" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={labelClasses}>Număr Identificare Card</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="0000 0000 0000 0000"
                      value={cardData.number}
                      onChange={handleCardNumberChange}
                      className={inputClasses}
                    />
                    <CreditCard size={16} className="absolute right-4 top-3.5 text-slate-600" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className={labelClasses}>Dată Expirare</label>
                    <input
                      type="text"
                      placeholder="MM/YY"
                      value={cardData.expiry}
                      onChange={handleExpiryChange}
                      className={inputClasses}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={labelClasses}>Cod Securitate</label>
                    <div className="relative">
                      <input
                        type="password"
                        placeholder="CVV/CVC"
                        value={cardData.cvv}
                        onChange={handleCvvChange}
                        className={inputClasses}
                      />
                      <Lock size={16} className="absolute right-4 top-3.5 text-slate-600" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 mt-auto">
                <div className="bg-slate-950/50 border border-slate-800 rounded-2xl p-5 mb-5">
                  <div className="flex justify-between items-end mb-1">
                    <span className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">Suma de Plată</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-black text-white italic tracking-tighter">€{totalAmount.toFixed(2)}</span>
                      <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">{currency}</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleFinalize}
                  disabled={isProcessing}
                  className="w-full py-5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-600 rounded-xl font-black uppercase tracking-[0.2em] text-xs h-auto flex items-center justify-center gap-2 transition-all shadow-xl shadow-emerald-900/30 active:scale-95"
                >
                  {isProcessing ? (
                    <span className="flex items-center gap-2"><Plane className="animate-bounce" size={16} /> Procesare...</span>
                  ) : (
                    <>Confirmă Plata <ShieldCheck size={18} /></>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer info - NTP Logo */}
        <div className="px-6 py-4 bg-slate-950/60 border-t border-slate-800 flex items-center justify-center gap-2">
          <Lock size={12} className="text-slate-600" />
          <span className="text-[9px] font-mono text-slate-600 uppercase tracking-widest flex items-center gap-2">
            Plată Securizată // <NTPLogo color="#475569" version="orizontal" secret="161586" height={9} />
          </span>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;